import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import { auth } from '../../util/firebase';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const FormContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CardElementContainer = styled.div`
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
`;

const Button = styled.button`
  background-color: #4A6CF7;
  color: white;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:disabled {
    background-color: #cbd5e0;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #3b5bdb;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  margin-top: 10px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #48bb78;
  margin-top: 10px;
  font-size: 0.9rem;
`;

const CheckoutForm = ({ plan, price, priceId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create a customer in your backend
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to subscribe');
        setProcessing(false);
        return;
      }

      const token = await user.getIdToken();

      // Create a payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Create customer in Stripe
      const customerResponse = await fetch(`${API_BASE_URL}/create-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const customer = await customerResponse.json();
      if (!customerResponse.ok || customer.error) {
        setError(customer?.error?.message || 'Unable to create customer');
        setProcessing(false);
        return;
      }

      // Create subscription
      const subscriptionResponse = await fetch(`${API_BASE_URL}/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          customerId: customer.id,
          priceId: priceId,
        }),
      });

      const subscription = await subscriptionResponse.json();

      if (!subscriptionResponse.ok || subscription.error) {
        setError(subscription?.error?.message || 'Unable to create subscription');
        setProcessing(false);
        return;
      }

      setSuccess(true);
      onSuccess();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Payment error:', err);
    }

    setProcessing(false);
  };

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <CardElementContainer>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </CardElementContainer>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Payment successful! Your subscription is now active.</SuccessMessage>}
        
        <Button type="submit" disabled={!stripe || processing}>
          {processing ? 'Processing...' : `Subscribe to ${plan} Plan - $${price}/month`}
        </Button>
      </Form>
    </FormContainer>
  );
};

export default CheckoutForm; 
